import { Control, useController, FieldPath } from 'react-hook-form';
import { AceGenericFieldLayout } from './AceGenericFieldLayout';
import { ReactNode, useMemo } from 'react';
import { Select } from '@blueprintjs/select';
import { Button, MenuItem } from '@blueprintjs/core';
import { defaultFormatter } from './common';

interface AceSelectFieldProps<T extends object, V> {
  control: Control<T>;
  name: FieldPath<T>;
  formatter?: (value: V) => ReactNode;
  options: Array<{
    value: V;
    label?: ReactNode;
    visible?: boolean;
  }>;
  editable?: boolean;
  label?: ReactNode;
  warning?: ReactNode;
  vertical?: boolean;
}

export function AceSelectField<T extends object, V>({
  control,
  name,
  editable,
  label,
  options,
  formatter = defaultFormatter,
  warning,
  vertical,
}: AceSelectFieldProps<T, V>) {
  const { field, fieldState } = useController({ control, name });

  const visibleOptions = useMemo(() => {
    const collator = new Intl.Collator(navigator.language);

    return options
      .filter(({ visible = true }) => visible)
      .map((option) => ({
        value: option.value,
        label:
          option.label !== undefined ? option.label : formatter(option.value),
      }))
      .sort(({ label: labelA }, { label: labelB }) =>
        collator.compare(String(labelA), String(labelB))
      );
  }, [formatter, options]);

  const formattedValue =
    options.find(({ value }) => value === field.value)?.label ??
    formatter(field.value);

  return (
    <AceGenericFieldLayout
      editable={editable}
      label={label}
      error={fieldState.error?.message}
      warning={warning}
      vertical={vertical}
      valueElement={formattedValue}
      inputElement={
        <Select<(typeof options)[number]>
          items={visibleOptions}
          itemRenderer={(option, { ref, index, handleClick }) => (
            <MenuItem
              text={option.label}
              ref={ref}
              key={index}
              onClick={handleClick}
            />
          )}
          filterable={false}
          onItemSelect={(option) => field.onChange(option.value)}
          activeItem={
            visibleOptions.find((option) => option.value === field.value) ??
            null
          }
          popoverProps={{
            minimal: true,
            onClose: field.onBlur,
          }}
        >
          <Button
            alignText="left"
            text={formattedValue}
            endIcon="double-caret-vertical"
            fill
          />
        </Select>
      }
    />
  );
}
